/**
 * @fileoverview Animation Pipeline & Typewriter Engine for Chat V2 Messages.
 * Ports the Flutter mobile `MessageAnimationPipeline` to Angular 21 with:
 * 1. Sequential phase execution: typing -> attachments -> interactions -> actions -> completed.
 * 2. 60fps chunked typewriter reveal (~16ms per frame) with adaptive speed for long texts.
 * 3. Instant skip-on-click mechanism allowing users to instantly reveal full text.
 * 4. Markdown syntax balancing to prevent broken tags during partial streaming.
 */

import { signal, computed } from '@angular/core';

/**
 * Enumeration of distinct phases in the message animation lifecycle.
 */
export type ChatV2AnimationPhase =
  | 'idle'
  | 'typing'
  | 'attachments'
  | 'interactions'
  | 'data'
  | 'actions'
  | 'completed';

/**
 * Configuration options for the message animation pipeline.
 */
export interface ChatV2AnimationConfig {
  /** Target frame interval in milliseconds (default: 16ms ~ 60fps) */
  frameIntervalMs: number;
  /** Minimum characters to reveal per tick for short texts (default: 3) */
  minCharsPerTick: number;
  /** Maximum characters to reveal per tick for long texts (default: 8) */
  maxCharsPerTick: number;
  /** Text length threshold to switch to fast chunk typing (default: 400 chars) */
  fastThresholdLength: number;
}

/** Default animation configuration values */
export const DEFAULT_ANIMATION_CONFIG: ChatV2AnimationConfig = {
  frameIntervalMs: 16,
  minCharsPerTick: 3,
  maxCharsPerTick: 8,
  fastThresholdLength: 400,
} as const;

/**
 * PURE FUNCTION: Calculates the optimal number of characters to advance per animation tick
 * based on the remaining text length.
 *
 * @param totalLength - Total character length of the full text.
 * @param currentIndex - Currently revealed character index.
 * @param config - Pipeline animation configuration.
 * @returns Number of characters to advance in the current tick.
 */
export function calculateAdvanceStep(
  totalLength: number,
  currentIndex: number,
  config: ChatV2AnimationConfig = DEFAULT_ANIMATION_CONFIG,
): number {
  if (currentIndex >= totalLength) {
    return 0;
  }
  const remaining = totalLength - currentIndex;
  if (totalLength > config.fastThresholdLength) {
    return Math.min(remaining, config.maxCharsPerTick);
  }
  return Math.min(remaining, config.minCharsPerTick);
}

/**
 * PURE FUNCTION: Balances unclosed triple backtick code blocks in partial streaming text
 * to prevent markdown layout jitter during typewriter animation.
 *
 * @param text - Partial markdown text snippet.
 * @returns Balanced text safe for markdown rendering.
 */
export function balanceMarkdownCodeBlocks(text: string): string {
  if (!text || text.indexOf('```') === -1) {
    return text;
  }
  const matches = text.match(/```/g);
  if (matches && matches.length % 2 !== 0) {
    return text + '\n```';
  }
  return text;
}

/**
 * @class ChatV2AnimationPipeline
 * @description Manages the phased entrance animations and typewriter text streaming for Chat V2 messages.
 */
export class ChatV2AnimationPipeline {
  /** Active animation phase signal */
  public readonly phase = signal<ChatV2AnimationPhase>('idle');

  /** Currently revealed text for the typewriter display */
  public readonly displayedText = signal<string>('');

  /** Whether the typewriter is actively advancing */
  public readonly isTyping = computed(() => this.phase() === 'typing');

  /** Whether all animation phases have concluded */
  public readonly isCompleted = computed(() => this.phase() === 'completed');

  /** Whether attachments are cleared to be shown */
  public readonly showAttachments = computed(() => {
    const current = this.phase();
    return (
      current === 'attachments' ||
      current === 'interactions' ||
      current === 'data' ||
      current === 'actions' ||
      current === 'completed'
    );
  });

  /** Whether interaction widgets are cleared to be shown */
  public readonly showInteractions = computed(() => {
    const current = this.phase();
    return (
      current === 'interactions' ||
      current === 'data' ||
      current === 'actions' ||
      current === 'completed'
    );
  });

  /** Whether entity data and tabular cards are cleared to be shown */
  public readonly showData = computed(() => {
    const current = this.phase();
    return (
      current === 'data' || current === 'actions' || current === 'completed'
    );
  });

  /** Whether workflow plan cards are cleared to be shown */
  public readonly showWorkflowPlan = computed(() => {
    const current = this.phase();
    return (
      current === 'data' || current === 'actions' || current === 'completed'
    );
  });

  /** Whether action cards are cleared to be shown */
  public readonly showActions = computed(() => {
    const current = this.phase();
    return current === 'actions' || current === 'completed';
  });

  /** Internal reference to the animation interval timer */
  private _timer: ReturnType<typeof setInterval> | null = null;

  /** Full target text to type out */
  private _fullText = '';

  /** Current character position index */
  private _charIndex = 0;

  /** Active animation configuration */
  private readonly _config: ChatV2AnimationConfig;

  /** Callback fired upon full pipeline completion */
  private _onCompleteCallback?: () => void;

  /** Callback fired on each typewriter tick or phase transition for smooth container auto-scrolling */
  private _onProgressCallback?: () => void;

  /**
   * Constructs the animation pipeline instance.
   *
   * @param config - Optional configuration overrides.
   */
  constructor(config: Partial<ChatV2AnimationConfig> = {}) {
    this._config = { ...DEFAULT_ANIMATION_CONFIG, ...config };
  }

  /**
   * Initializes and starts the phased animation pipeline for an AI message.
   *
   * @param fullText - Complete response text to type out.
   * @param shouldAnimate - Whether animation is permitted (e.g. latest un-animated message).
   * @param onComplete - Optional callback invoked when all phases finish.
   * @param onProgress - Optional callback invoked on typing advances and phase reveals for auto-scroll.
   */
  public start(
    fullText: string,
    shouldAnimate: boolean,
    onComplete?: () => void,
    onProgress?: () => void,
  ): void {
    this.stop();
    this._fullText = fullText || '';
    this._onCompleteCallback = onComplete;
    this._onProgressCallback = onProgress;

    if (!shouldAnimate) {
      this._finishImmediately();
      return;
    }

    if (!this._fullText) {
      // Phased reveal for attachments, data, and actions when text is empty (matching Flutter _hasAnimatableContent)
      this.phase.set('attachments');
      this._onProgressCallback?.();
      setTimeout(() => {
        this.phase.set('data');
        this._onProgressCallback?.();
        setTimeout(() => {
          this.phase.set('actions');
          this._onProgressCallback?.();
          setTimeout(() => {
            this.phase.set('completed');
            this._onProgressCallback?.();
            this._onCompleteCallback?.call(null);
          }, 50);
        }, 50);
      }, 50);
      return;
    }

    this._charIndex = 0;
    this.displayedText.set('');
    this.phase.set('typing');
    this._onProgressCallback?.();

    this._timer = setInterval(() => {
      this._advanceTyping();
    }, this._config.frameIntervalMs);
  }

  /**
   * Advances typing by the calculated step for the current tick.
   */
  private _advanceTyping(): void {
    const total = this._fullText.length;
    if (this._charIndex >= total) {
      this._completeTypingPhase();
      return;
    }

    const step = calculateAdvanceStep(total, this._charIndex, this._config);
    this._charIndex = Math.min(total, this._charIndex + step);
    const slice = this._fullText.slice(0, this._charIndex);
    this.displayedText.set(balanceMarkdownCodeBlocks(slice));
    this._onProgressCallback?.();

    if (this._charIndex >= total) {
      this._completeTypingPhase();
    }
  }

  /**
   * Transitions from typing phase through secondary phases (attachments -> interactions -> data -> actions) to completed.
   */
  private _completeTypingPhase(): void {
    this.stopTimer();
    this.displayedText.set(this._fullText);
    this.phase.set('attachments');
    this._onProgressCallback?.();

    // Smooth micro-stagger for subsequent phases matching mobile UX
    setTimeout(() => {
      this.phase.set('interactions');
      this._onProgressCallback?.();
      setTimeout(() => {
        this.phase.set('data');
        this._onProgressCallback?.();
        setTimeout(() => {
          this.phase.set('actions');
          this._onProgressCallback?.();
          setTimeout(() => {
            this.phase.set('completed');
            this._onProgressCallback?.();
            this._onCompleteCallback?.call(null);
          }, 50);
        }, 50);
      }, 50);
    }, 50);
  }

  /**
   * Instantly completes all animation phases (e.g. for historical messages or user skip-click).
   */
  public skipToEnd(): void {
    this._finishImmediately();
  }

  /**
   * Sets full text and marks pipeline immediately as completed.
   */
  private _finishImmediately(): void {
    this.stopTimer();
    this.displayedText.set(this._fullText);
    this.phase.set('completed');
    this._onCompleteCallback?.call(null);
  }

  /**
   * Stops active timer without changing phase.
   */
  public stopTimer(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  /**
   * Completely halts pipeline and tears down timers on component destruction.
   */
  public stop(): void {
    this.stopTimer();
  }
}
